import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";



import styled from "styled-components";



import { useMediaSettings } from "Signups/stateHooks";
import { AnimatePresence, motion } from "framer-motion";



import FnrInput2 from "UI/FnrInput2";



import { useDebounce } from "Utilities/Hooks/useDebounce";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";
import { generateEmbedUrl, getVideoSource, validateVideoLink } from "Utilities/VideoUtils";



import { RemoveButtonWrapper, RemoveMediaButton } from "./RemoveMediaButton";
import bgImage from "./video_bg.jpg";


export default function VideoInput({ setError, showingColorPicker }) {
  const { mediaSettings, isLoading, hasError, setMediaSettings } =
    useMediaSettings();
  const embedUrl = mediaSettings?.videoUrl;
  const [value, setValue] = useState(mediaSettings?.videoUrl || "");
  const [isValidUrl, setIsValidUrl] = useState(true);
  const [videoSource, setVideoSource] = useState(null);
  const [editMode, setEditMode] = useState(
    mediaSettings?.videoUrl ? false : true
  );
  const wrapperRef = useRef();

  const isHoverFocused = useIsHoverFocused(wrapperRef);

  useEffect(() => {
    if (isHoverFocused) setEditMode(isHoverFocused);
  }, [isHoverFocused]);

  useOnClickOutside(wrapperRef, (e) => {
    setEditMode(false);
  });

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, [setIsMounted]);

  const handleChange = (e) => {
    setIsValidUrl(true);
    setValue(e.target.value);
  };

  const handlePaste = (e) => {
    const newValue = e.clipboardData.getData("Text");
    setValue(newValue);
    handleSave(newValue);
  };

  const handleSave = async (newValue) => {
    if (newValue.trim() === "") return;

    const isValid = await validateVideoLink(newValue);
    if (!isValid) {
      setIsValidUrl(false);
      return;
    }

    const embUrl = await generateEmbedUrl(newValue);
    setError(undefined);
    setMediaSettings({ ...mediaSettings, videoUrl: embUrl });
  };

  // on enter, save
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave(value);
    }
  };

  const getVideoSrcData = useCallback(async () => {
    const vidSource = await getVideoSource(value);
    setVideoSource(vidSource);
  }, [value]);

  // update to saved value when mediaSettings changes
  useEffect(() => {
    setValue(mediaSettings?.videoUrl || "");
  }, [mediaSettings]);

  // update video source on first load and when it changes
  useEffect(() => {
    getVideoSrcData(value);
  }, [value, getVideoSrcData]);

  // sync error to isValidUrl and hasError
  useEffect(() => {
    if (!isValidUrl) {
      setError("Only YouTube or Vimeo video links are valid.");
    } else if (hasError) {
      setError("Something went wrong saving your video. Please try again.");
    } else if (isMounted) {
      setError(null);
    }
  }, [isValidUrl, hasError, setError, isMounted]);

  const hasVideo = embedUrl && isValidUrl;
  const top = hasVideo ? 0 : "auto";
  const parentMarginTop = hasVideo ? "60px" : "0";

  return (
    <OuterWrapper animate={{ paddingTop: hasVideo ? 60 : 0 }} ref={wrapperRef}>
      <AnimatePresence>
        <InputWrapper
          initial={{ opacity: 0, top, width: "80%" }}
          animate={{
            top,
            opacity: editMode || hasVideo ? 1 : 0,
            width: hasVideo ? "100%" : "80%",
          }}
          exit={{ opacity: 0 }}
        >
          <FnrInput2
            data-cy="Signup_VideoInput"
            icon={videoSource ? `fa-${videoSource}-square fab` : ""}
            iconColor="textBlack"
            autoFocus
            type="text"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            value={value}
            placeholder="Type or Paste a Video Link"
            disabled={isLoading}
          />
        </InputWrapper>
        {!editMode && (
          <ClickCapture key="click-capture" onClick={() => setEditMode(true)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {hasVideo ? (
          <VideoWrapper
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <VideoFrame
              data-cy="Signup_VideoFrame"
              src={embedUrl}
              title={`${videoSource} video player`}
              allow="picture-in-picture;"
              allowFullScreen
              $showingColorPicker={showingColorPicker}
            />
          </VideoWrapper>
        ) : (
          <DefaultBG
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            $backgroundImage={bgImage}
          />
        )}
      </AnimatePresence>

      <SRemoveButtonWrapper $hasVideo={hasVideo}>
        <RemoveMediaButton />
      </SRemoveButtonWrapper>
    </OuterWrapper>
  );
}

VideoInput.propTypes = {
  $colorVariant: PropTypes.string,
  setError: PropTypes.func.isRequired,
  showingColorPicker: PropTypes.bool,
};

const OuterWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`;

const DefaultBG = styled(motion.div)`
  position: relative;
  background-image: url(${bgImage});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ClickCapture = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  border-radius: 8px;
`;

const InputWrapper = styled(motion.div)`
  position: absolute;
  z-index: 100;
`;

const VideoWrapper = styled(motion.div)`
  background: #000;
  flex-shrink: 0;
  width: calc(100%);
  height: 100%;
  border-radius: 6px;
  box-shadow: rgba(0, 0, 0, 0.6) 1px 1px 12px;
`;

const VideoFrame = styled.iframe`
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: none;
  display: block;
  pointer-events: ${({ $showingColorPicker }) =>
    $showingColorPicker ? "none" : "auto"};
`;

const SRemoveButtonWrapper = styled(RemoveButtonWrapper)`
  top: ${({ $hasVideo }) => ($hasVideo ? "68px" : "6px")};
  right: 6px;

  ${OuterWrapper}:hover & {
    opacity: 1;
  }
`;